import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAllPlanningCycles,
  getAllPlanningCycleSnapshots,
  getForecastingPlanningCycles,
  getScenarios,
  planningCycleFormMutation,
  refreshFromLiveMutation
} from 'src/api/app-sync-services';
import { ForecastPlanningCycleIds, PlanningCycleEntity, PlanningCycleSnapshot } from 'src/models/PlanningCycleModel';
import { getForecastS3BucketName } from 'src/utils/xpt-s3-bucket-details';

/**
 * Async thunk for fetching planning cycles from the backend.
 */
export const fetchPlanningCycles = createAsyncThunk('planningCycle/fetchPlanningCycles', async (_, { rejectWithValue }) => {
  try {
    const allPlanningCycles = await getAllPlanningCycles();

    return allPlanningCycles.map((planningCycle: PlanningCycleEntity) => ({
      ...planningCycle,
      scenario_year: `${planningCycle.scenario.planning_cycle_name}_${planningCycle.planning_cycle_year}`,
      export_s3_bucket: getForecastS3BucketName().bucketName,
      extended_users: planningCycle.extended_user_lists
        ? planningCycle.extended_user_lists.map((user) => ({
            label: user,
            value: user
          }))
        : []
    }));
  } catch (error) {
    return rejectWithValue('Failed to fetch planning cycles');
  }
});

/**
 * Async thunk for fetching forecast planning cycles.
 */
export const fetchForecastPlanningCycles = createAsyncThunk('planningCycle/fetchForecastPlanningCycles', async (_, { rejectWithValue }) => {
  try {
    const forecastPlanningCycles: ForecastPlanningCycleIds[] = await getForecastingPlanningCycles();
    return forecastPlanningCycles;
  } catch (error) {
    return rejectWithValue('Failed to fetch forecast planning cycles');
  }
});

/**
 * Async thunk for fetching scenarios related to planning cycles.
 */
export const fetchScenarios = createAsyncThunk('planningCycle/fetchScenarios', async (_, { rejectWithValue }) => {
  try {
    const scenarios = await getScenarios();
    return scenarios;
  } catch (error) {
    return rejectWithValue('Failed to fetch scenarios');
  }
});

/**
 * Async thunk for fetching planning cycle snapshots.
 */
export const fetchSnapshots = createAsyncThunk('planningCycle/fetchSnapshots', async (_, { rejectWithValue }) => {
  try {
    const planningCycleSnapshots: PlanningCycleSnapshot[] = await getAllPlanningCycleSnapshots();
    return planningCycleSnapshots;
  } catch (error) {
    return rejectWithValue('Failed to fetch planning cycle snapshots');
  }
});

/**
 * Async thunk for planning cycle form submission
 */
export const submitPlanningCycle = createAsyncThunk(
  'planningCycle/submitPlanningCycle',
  async (planningCycleForm: PlanningCycleEntity[], { rejectWithValue }) => {
    try {
      const response = await planningCycleFormMutation(planningCycleForm);
      return response;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unable to update planning cycle';
      return rejectWithValue(message);
    }
  }
);

/**
 * Async thunk: refresh an OP planning cycle's forecast data from the active R&O (live) scenario.
 */
export const refreshFromLive = createAsyncThunk(
  'planningCycle/refreshFromLive',
  async (
    { scenario_seq_id, updated_by, export_s3_bucket }: { scenario_seq_id: number; updated_by: string; export_s3_bucket: string },
    { rejectWithValue }
  ) => {
    try {
      return await refreshFromLiveMutation(scenario_seq_id, updated_by, export_s3_bucket);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unable to refresh from live R&O scenario';
      return rejectWithValue(message);
    }
  }
);
